import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

const creatableTypes = [
  'dte_roll_window',
  'leg_delta',
  'position_exposure',
  'profit_target',
  'max_loss',
  'strike_proximity',
  'margin_risk',
];

const blankForm = {
  alert_type: 'dte_roll_window',
  scope_type: 'operation',
  operation_id: '',
  strategy_label: '',
  underlying: 'BTC',
  channels: { in_app: true, email: false },
  threshold_value: '21',
  comparator: 'lte',
  leg_scope: 'any_short',
  mode: 'threshold',
  target_bias: 'bullish',
  unit: 'native',
  target: 'any_short',
  metric: 'available_funds_native',
};

const toPercentDecimal = (value) => Number(value || 0) / 100;

export const OptionsAlertRuleDialog = ({ open, onOpenChange, onSave, initialRule = null, targets, preferences }) => {
  const { t } = useOptionsLocale();
  const [form, setForm] = useState(blankForm);

  useEffect(() => {
    if (!initialRule) {
      setForm(blankForm);
      return;
    }
    const condition = initialRule.condition || {};
    setForm({
      alert_type: initialRule.alert_type,
      scope_type: initialRule.scope_type,
      operation_id: initialRule.operation_id || '',
      strategy_label: initialRule.strategy_label || '',
      underlying: initialRule.underlying || 'BTC',
      channels: initialRule.channels || { in_app: true, email: false },
      threshold_value: String(
        ['position_exposure', 'strike_proximity'].includes(initialRule.alert_type)
          ? Number(condition.threshold_value || 0) * 100
          : condition.threshold_value ?? ''
      ),
      comparator: condition.comparator || 'lte',
      leg_scope: condition.leg_scope || 'any_short',
      mode: condition.mode || 'threshold',
      target_bias: condition.target_bias || 'bullish',
      unit: condition.unit || 'native',
      target: condition.target || 'any_short',
      metric: condition.metric || 'available_funds_native',
    });
  }, [initialRule]);

  const availableOperations = useMemo(() => targets?.operations || [], [targets?.operations]);
  const availableStrategies = useMemo(() => targets?.strategies || [], [targets?.strategies]);
  const availableUnderlyings = useMemo(() => targets?.underlyings || ['BTC', 'ETH'], [targets?.underlyings]);
  const marginCurrencies = useMemo(() => targets?.margin_currencies || ['BTC', 'ETH'], [targets?.margin_currencies]);

  const dialogTitle = initialRule ? t('optionsAlerts.editAlert') : t('optionsAlerts.createAlert');

  const payload = useMemo(() => {
    const base = {
      enabled: true,
      alert_type: form.alert_type,
      scope_type: form.scope_type,
      operation_id: form.scope_type === 'operation' ? form.operation_id : null,
      scope_id: form.scope_type === 'operation'
        ? form.operation_id
        : form.scope_type === 'strategy_default'
          ? form.strategy_label
          : form.underlying,
      strategy_label: form.scope_type === 'strategy_default' ? form.strategy_label : null,
      underlying: ['underlying', 'account'].includes(form.scope_type) ? form.underlying : (form.operation_id ? (availableOperations.find((item) => item.id === form.operation_id)?.underlying || null) : null),
      channels: {
        in_app: !!form.channels.in_app,
        email: !!form.channels.email && !!preferences?.email_enabled,
      },
      metadata: {
        locale: preferences?.locale || 'ptBR',
        timezone: preferences?.timezone || 'UTC',
      },
    };

    if (form.alert_type === 'dte_roll_window') {
      return { ...base, condition: { threshold_value: Number(form.threshold_value || 0) } };
    }
    if (form.alert_type === 'leg_delta') {
      return { ...base, condition: { threshold_value: Number(form.threshold_value || 0), leg_scope: form.leg_scope } };
    }
    if (form.alert_type === 'position_exposure') {
      return {
        ...base,
        scope_type: 'underlying',
        scope_id: form.underlying,
        underlying: form.underlying,
        condition: form.mode === 'bias'
          ? { mode: 'bias', target_bias: form.target_bias }
          : { mode: 'threshold', comparator: form.comparator, threshold_value: toPercentDecimal(form.threshold_value) },
      };
    }
    if (form.alert_type === 'profit_target' || form.alert_type === 'max_loss') {
      return {
        ...base,
        condition: { unit: form.unit, comparator: form.alert_type === 'profit_target' ? 'gte' : 'lte', threshold_value: Number(form.threshold_value || 0) },
      };
    }
    if (form.alert_type === 'strike_proximity') {
      return { ...base, condition: { target: form.target, threshold_value: toPercentDecimal(form.threshold_value) } };
    }
    return {
      ...base,
      scope_type: 'account',
      scope_id: form.underlying,
      underlying: form.underlying,
      condition: {
        metric: form.metric,
        comparator: form.metric === 'available_funds_native' ? 'lte' : 'gte',
        threshold_value: form.metric.includes('ratio') ? toPercentDecimal(form.threshold_value) : Number(form.threshold_value || 0),
        unit: form.metric.includes('ratio') ? 'ratio' : 'native',
      },
    };
  }, [availableOperations, form, preferences]);

  const handleSave = async () => {
    await onSave(payload);
  };

  const showOperationScope = ['dte_roll_window', 'leg_delta', 'profit_target', 'max_loss', 'strike_proximity'].includes(form.alert_type);
  const showStrategyScope = ['dte_roll_window', 'leg_delta'].includes(form.alert_type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#07111f] text-white sm:max-w-[680px]" data-testid="options-alert-rule-dialog">
        <DialogHeader>
          <DialogTitle data-testid="options-alert-rule-dialog-title">{dialogTitle}</DialogTitle>
          <DialogDescription className="text-white/60" data-testid="options-alert-rule-dialog-description">
            {t('optionsAlerts.dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label={t('optionsAlerts.type')} testId="options-alert-rule-dialog-type">
            <Select value={form.alert_type} onValueChange={(value) => setForm((current) => ({ ...current, alert_type: value, scope_type: ['position_exposure'].includes(value) ? 'underlying' : value === 'margin_risk' ? 'account' : current.scope_type }))}>
              <SelectTrigger data-testid="options-alert-rule-dialog-type-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {creatableTypes.map((value) => (
                  <SelectItem key={value} value={value}>{t(`optionsAlerts.types.${value}.label`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {(showOperationScope || showStrategyScope) ? (
            <Field label={t('optionsAlerts.scope')} testId="options-alert-rule-dialog-scope">
              <Select value={form.scope_type} onValueChange={(value) => setForm((current) => ({ ...current, scope_type: value }))}>
                <SelectTrigger data-testid="options-alert-rule-dialog-scope-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {showOperationScope ? <SelectItem value="operation">{t('optionsAlerts.scopeOperation')}</SelectItem> : null}
                  {showStrategyScope ? <SelectItem value="strategy_default">{t('optionsAlerts.scopeStrategyDefault')}</SelectItem> : null}
                </SelectContent>
              </Select>
            </Field>
          ) : null}

          {form.scope_type === 'operation' ? (
            <Field label={t('optionsAlerts.operation')} testId="options-alert-rule-dialog-operation">
              <Select value={form.operation_id} onValueChange={(value) => setForm((current) => ({ ...current, operation_id: value }))}>
                <SelectTrigger data-testid="options-alert-rule-dialog-operation-trigger"><SelectValue placeholder={t('optionsAlerts.selectOperation')} /></SelectTrigger>
                <SelectContent>
                  {availableOperations.map((item) => <SelectItem key={item.id} value={item.id}>{item.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          ) : null}

          {form.scope_type === 'strategy_default' ? (
            <Field label={t('optionsAlerts.strategy')} testId="options-alert-rule-dialog-strategy">
              <Select value={form.strategy_label} onValueChange={(value) => setForm((current) => ({ ...current, strategy_label: value }))}>
                <SelectTrigger data-testid="options-alert-rule-dialog-strategy-trigger"><SelectValue placeholder={t('optionsAlerts.selectStrategy')} /></SelectTrigger>
                <SelectContent>
                  {availableStrategies.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          ) : null}

          {['position_exposure', 'margin_risk'].includes(form.alert_type) ? (
            <Field label={form.alert_type === 'margin_risk' ? t('optionsAlerts.currency') : t('optionsAlerts.underlying')} testId="options-alert-rule-dialog-underlying">
              <Select value={form.underlying} onValueChange={(value) => setForm((current) => ({ ...current, underlying: value }))}>
                <SelectTrigger data-testid="options-alert-rule-dialog-underlying-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(form.alert_type === 'margin_risk' ? marginCurrencies : availableUnderlyings).map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          ) : null}

          {form.alert_type === 'position_exposure' ? (
            <>
              <Field label={t('optionsAlerts.mode')} testId="options-alert-rule-dialog-mode">
                <Select value={form.mode} onValueChange={(value) => setForm((current) => ({ ...current, mode: value }))}>
                  <SelectTrigger data-testid="options-alert-rule-dialog-mode-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="threshold">{t('optionsAlerts.exposureThreshold')}</SelectItem>
                    <SelectItem value="bias">{t('optionsAlerts.biasState')}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {form.mode === 'bias' ? (
                <Field label={t('optionsAlerts.targetBias')} testId="options-alert-rule-dialog-target-bias">
                  <Select value={form.target_bias} onValueChange={(value) => setForm((current) => ({ ...current, target_bias: value }))}>
                    <SelectTrigger data-testid="options-alert-rule-dialog-target-bias-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['neutral', 'partially_bullish', 'bullish', 'strongly_bullish', 'partially_bearish', 'bearish', 'strongly_bearish'].map((value) => <SelectItem key={value} value={value}>{t(`optionsAlerts.bias.${value}`)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              ) : (
                <>
                  <Field label={t('optionsAlerts.direction')} testId="options-alert-rule-dialog-direction">
                    <Select value={form.comparator} onValueChange={(value) => setForm((current) => ({ ...current, comparator: value }))}>
                      <SelectTrigger data-testid="options-alert-rule-dialog-direction-trigger"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gte">&gt;=</SelectItem>
                        <SelectItem value="lte">&lt;=</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={t('optionsAlerts.thresholdPercent')} testId="options-alert-rule-dialog-threshold-percent">
                    <NumberInput value={form.threshold_value} onChange={(value) => setForm((current) => ({ ...current, threshold_value: value }))} testId="options-alert-rule-dialog-threshold-percent-input" />
                  </Field>
                </>
              )}
            </>
          ) : null}

          {form.alert_type === 'leg_delta' ? (
            <>
              <Field label={t('optionsAlerts.legScope')} testId="options-alert-rule-dialog-leg-scope">
                <Select value={form.leg_scope} onValueChange={(value) => setForm((current) => ({ ...current, leg_scope: value }))}>
                  <SelectTrigger data-testid="options-alert-rule-dialog-leg-scope-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any_short">{t('optionsAlerts.legScopes.any_short')}</SelectItem>
                    <SelectItem value="short_call">{t('optionsAlerts.legScopes.short_call')}</SelectItem>
                    <SelectItem value="short_put">{t('optionsAlerts.legScopes.short_put')}</SelectItem>
                    <SelectItem value="any_long">{t('optionsAlerts.legScopes.any_long')}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t('optionsAlerts.threshold')} testId="options-alert-rule-dialog-threshold">
                <NumberInput value={form.threshold_value} onChange={(value) => setForm((current) => ({ ...current, threshold_value: value }))} testId="options-alert-rule-dialog-threshold-input" />
              </Field>
            </>
          ) : null}

          {form.alert_type === 'dte_roll_window' ? (
            <Field label={t('optionsAlerts.thresholdDte')} testId="options-alert-rule-dialog-threshold-dte">
              <NumberInput value={form.threshold_value} onChange={(value) => setForm((current) => ({ ...current, threshold_value: value }))} testId="options-alert-rule-dialog-threshold-dte-input" />
            </Field>
          ) : null}

          {['profit_target', 'max_loss'].includes(form.alert_type) ? (
            <>
              <Field label={t('optionsAlerts.unit')} testId="options-alert-rule-dialog-unit">
                <Select value={form.unit} onValueChange={(value) => setForm((current) => ({ ...current, unit: value }))}>
                  <SelectTrigger data-testid="options-alert-rule-dialog-unit-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="native">{t('optionsAlerts.unitNative')}</SelectItem>
                    <SelectItem value="usd">USD</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t('optionsAlerts.threshold')} testId="options-alert-rule-dialog-threshold-generic">
                <NumberInput value={form.threshold_value} onChange={(value) => setForm((current) => ({ ...current, threshold_value: value }))} testId="options-alert-rule-dialog-threshold-generic-input" />
              </Field>
            </>
          ) : null}

          {form.alert_type === 'strike_proximity' ? (
            <>
              <Field label={t('optionsAlerts.target')} testId="options-alert-rule-dialog-target">
                <Select value={form.target} onValueChange={(value) => setForm((current) => ({ ...current, target: value }))}>
                  <SelectTrigger data-testid="options-alert-rule-dialog-target-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any_short">{t('optionsAlerts.strikeTargets.any_short')}</SelectItem>
                    <SelectItem value="short_call">{t('optionsAlerts.strikeTargets.short_call')}</SelectItem>
                    <SelectItem value="short_put">{t('optionsAlerts.strikeTargets.short_put')}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t('optionsAlerts.thresholdPercent')} testId="options-alert-rule-dialog-threshold-strike">
                <NumberInput value={form.threshold_value} onChange={(value) => setForm((current) => ({ ...current, threshold_value: value }))} testId="options-alert-rule-dialog-threshold-strike-input" />
              </Field>
            </>
          ) : null}

          {form.alert_type === 'margin_risk' ? (
            <>
              <Field label={t('optionsAlerts.metric')} testId="options-alert-rule-dialog-metric">
                <Select value={form.metric} onValueChange={(value) => setForm((current) => ({ ...current, metric: value }))}>
                  <SelectTrigger data-testid="options-alert-rule-dialog-metric-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available_funds_native">{t('optionsAlerts.marginMetrics.available_funds_native')}</SelectItem>
                    <SelectItem value="maintenance_margin_ratio">{t('optionsAlerts.marginMetrics.maintenance_margin_ratio')}</SelectItem>
                    <SelectItem value="close_out_margin_ratio">{t('optionsAlerts.marginMetrics.close_out_margin_ratio')}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={form.metric.includes('ratio') ? t('optionsAlerts.thresholdPercent') : t('optionsAlerts.threshold')} testId="options-alert-rule-dialog-threshold-margin">
                <NumberInput value={form.threshold_value} onChange={(value) => setForm((current) => ({ ...current, threshold_value: value }))} testId="options-alert-rule-dialog-threshold-margin-input" />
              </Field>
            </>
          ) : null}
        </div>

        <div className="space-y-3 rounded-2xl border border-white/10 bg-black/20 p-4" data-testid="options-alert-rule-dialog-channels">
          <p className="text-sm font-semibold text-white">{t('optionsAlerts.channels')}</p>
          <ChannelRow label="GENI" checked={!!form.channels.in_app} onCheckedChange={(checked) => setForm((current) => ({ ...current, channels: { ...current.channels, in_app: checked } }))} testId="options-alert-rule-dialog-channel-in-app" />
          <ChannelRow label="Email" checked={!!form.channels.email} onCheckedChange={(checked) => setForm((current) => ({ ...current, channels: { ...current.channels, email: checked } }))} disabled={!preferences?.email_enabled} helper={!preferences?.email_enabled ? t('optionsAlerts.emailDisabledHelper') : null} testId="options-alert-rule-dialog-channel-email" />
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" className="text-white/70 hover:bg-white/10 hover:text-white" onClick={() => onOpenChange(false)} data-testid="options-alert-rule-dialog-cancel">
            {t('optionsAlerts.cancel')}
          </Button>
          <Button type="button" className="rounded-full bg-[#F0B90B] px-5 text-black hover:bg-[#ffd26a]" onClick={handleSave} data-testid="options-alert-rule-dialog-save">
            {t('optionsAlerts.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, testId, children }) => (
  <label className="space-y-2" data-testid={testId}>
    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/45">{label}</span>
    {children}
  </label>
);

const NumberInput = ({ value, onChange, testId }) => (
  <input value={value} onChange={(event) => onChange(event.target.value)} className="h-10 w-full rounded-xl border border-white/10 bg-black/20 px-3 text-sm text-white outline-none transition focus:border-[#F0B90B]" data-testid={testId} />
);

const ChannelRow = ({ label, checked, onCheckedChange, disabled = false, helper = null, testId }) => (
  <div className="flex items-start justify-between gap-3" data-testid={testId}>
    <div>
      <p className="text-sm font-medium text-white">{label}</p>
      {helper ? <p className="text-xs text-white/50">{helper}</p> : null}
    </div>
    <Checkbox checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
  </div>
);

export default OptionsAlertRuleDialog;